import React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@mui/material';
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from '@mui/lab';
import CheckIcon from '@mui/icons-material/Check';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import StopIcon from '@mui/icons-material/Stop';
import { deriveRunFlowNodeStatus } from 'redux/slices/pipelines/helpers/deriveRunFlowNodeStatus';
import BuildLogPanel from './BuildLogPanel';

// AC-2/AC-4/AC-6/AC-19/OQ-1c — the pipeline detail page's vertical stage timeline. Renders
// the exactly TWO phases the run engine executes — Build image, Deploy to container —
// never Checkout/Health-check (AC-19: no such phase exists anywhere in `PipelineRunStatus`).
// A vertical, non-wrapping `@mui/lab` Timeline structurally kills the old flexbox row's
// wrap-and-dangling-connector defect (AC-4). The guarantee "n stages -> n-1 connectors" is
// carried by the `!isLast &&` guard on the single `TimelineConnector` below — that guard is
// the thing to grep for and the thing a reviewer must not delete. It is a property of the
// markup rather than a visual promise: there is no arrangement of `stages` that can emit a
// connector after the final item.
const STAGE_META = {
	pending: { label: 'Not started', color: 'grey', variant: 'outlined', Icon: RadioButtonUncheckedIcon, textColor: 'text.secondary' },
	active: { label: 'In progress', color: 'info', variant: 'filled', Icon: AutorenewIcon, textColor: 'info.main' },
	success: { label: 'Succeeded', color: 'success', variant: 'filled', Icon: CheckIcon, textColor: 'success.main' },
	failed: { label: 'Failed', color: 'error', variant: 'filled', Icon: PriorityHighIcon, textColor: 'error.main' },
	// Epic-wide adversarial review (M1). A cancelled stage is neither `pending` ("Not started",
	// which is false for a build that ran) nor `failed` (which is the exact misreport the sixth
	// status exists to prevent — nothing went wrong, a person stopped it). Deliberately NOT
	// `error`-coloured for that reason: it reads as a neutral halt, matching the Cancelled chip's
	// own intent, and it is distinguishable from `pending` by icon and label, not by colour alone.
	cancelled: { label: 'Cancelled', color: 'grey', variant: 'filled', Icon: StopIcon, textColor: 'text.secondary' }
};

// AC-6 — the exact per-status fact table. No stage ever renders as a bare label with an
// empty body; `Queued` is the honest floor (both stages read "Not started" — a real state,
// not invented detail).
// AC-25 — `elapsedLabel` is the WHOLE RUN's elapsed time (the only one we have: no per-stage
// timestamps are persisted, so a genuine per-stage duration does not exist). Labelling it
// bare "elapsed" next to a stage would read as that stage's own duration — a fact the engine
// cannot supply. It is therefore worded "since the run started", which is both true and
// unambiguous about what it measures.
const activeStageFact = elapsedLabel => (elapsedLabel ? `In progress · ${elapsedLabel} since the run started` : 'In progress');

// The "image produced" clause asserts an ARTIFACT, so it reads the artifact field
// (`ImageId`, stamped only on build success) rather than inferring it from `Status`. The
// inference would almost always be right — the engine only reaches Deploying/Succeeded
// after a successful build — but this is the one stage fact that names a thing, and it
// should be checking the thing.
const buildStageFact = (status, elapsedLabel, hasImage) => {
	if (status === 'active') return activeStageFact(elapsedLabel);
	if (status === 'success') return hasImage ? 'Succeeded · image produced' : 'Succeeded';
	if (status === 'failed') return 'Failed';
	// M1: asserts only what is known — a build was started and the run was cancelled. Independent
	// review of the first version of this fix: it originally read "Cancelled before it finished",
	// which directly contradicted the BE on one reachable path. When the build completes between the
	// orchestrator's poll and the user's click, ACR answers the cancel with NothingToCancel and the
	// run's own ErrorMessage — rendered by CancelledRunNotice a few rows above this one — says the
	// build "had already finished at the registry". Two claims about the same build, on one screen.
	// This wording is true in both cases and leaves the notice to supply the detail it actually
	// knows, which is the same division of labour the failure banner already uses.
	if (status === 'cancelled') return 'Cancelled';
	return 'Not started';
};

const deployStageFact = (status, elapsedLabel) => {
	if (status === 'active') return activeStageFact(elapsedLabel);
	if (status === 'success') return 'Succeeded';
	if (status === 'failed') return 'Failed';
	// No `cancelled` arm here, deliberately: an independent review established that a Cancelled run
	// can never carry an ImageId, so the deploy stage of a cancelled run is always `pending`. A
	// 'Cancelled — not deployed' string existed here briefly and was dead code. If the deploy leg
	// ever becomes cancellable, add it back together with the BE change that makes it reachable.
	return 'Not started';
};

// deploy-pipelines-run-logs (Wave 3) AC-6/AC-17 — which stage, in which state, gets a log
// panel. Two independent conditions, both load-bearing:
//
//   `stage.key === 'build'` is AC-17, and it is absolute. The DEPLOY stage gets no log
//   affordance of ANY kind — not a panel, not a disabled control, not an empty state, not a
//   "logs unavailable" row. The deploy leg calls AppInstanceService to set the container to
//   Waiting and enqueue a redeploy; nothing captures step output because there is no step
//   output to capture. A failed deploy surfaces PipelineRun.ErrorMessage only, exactly as
//   Wave 1 shipped it. Do not "improve" this by giving Deploy a panel that says logs are
//   unavailable: an affordance that can never work is a worse answer than no affordance.
//
//   `stage.status !== 'pending'` is AC-6's fourth row. A Build stage that has not started
//   renders NO panel at all, rather than a collapsed empty one — and because the panel owns
//   the log hook, not rendering it is also what stops a request being issued for a run that
//   provably has no log source yet.
//   Epic-wide adversarial review (M1): `!== 'pending'` is kept verbatim rather than being turned
//   into an allow-list, because it is exactly right once `cancelled` is a real stage state. A
//   cancelled build (`cancelled`) HAS output and keeps its panel; a run cancelled while Queued
//   derives `pending` for Build and correctly renders no panel, since no registry build ever
//   existed. Before the `cancelled` state existed, every cancelled run took the `pending` branch
//   and lost its log panel in the same commit that the status arrived — including the common case
//   of stopping a build the user had been watching for minutes.
const shouldRenderBuildLog = stage => stage.key === 'build' && stage.status !== 'pending';

const PipelineFlow = ({ pipelineId, run, elapsedLabel }) => {
	// AC-9 — a pipeline that has never run renders ZERO stages, not a greyed placeholder.
	if (!run) {
		return null;
	}

	const { build: buildStatus, deploy: deployStatus, wholeFlowFailed } = deriveRunFlowNodeStatus(run);
	const effectiveBuild = wholeFlowFailed ? 'failed' : buildStatus;
	const effectiveDeploy = wholeFlowFailed ? 'failed' : deployStatus;
	const hasImage = run.ImageId !== undefined && run.ImageId !== null && run.ImageId !== '';

	const stages = [
		{ key: 'build', label: 'Build image', status: effectiveBuild, fact: buildStageFact(effectiveBuild, elapsedLabel, hasImage) },
		{ key: 'deploy', label: 'Deploy to container', status: effectiveDeploy, fact: deployStageFact(effectiveDeploy, elapsedLabel) }
	];

	// The reason is rendered ONCE, on the first failed stage. `deriveRunFlowNodeStatus`
	// normally fails exactly one stage, but its `wholeFlowFailed` fallback fails BOTH — and
	// that path becomes reachable the moment the BE adds a `PipelineRunStatus` member the FE
	// doesn't know yet (the Wave-5 `Cancelled` state is the obvious candidate). Without this
	// guard that future would print the same error twice in the timeline, on top of the
	// above-the-fold banner.
	const firstFailedIndex = stages.findIndex(stage => stage.status === 'failed');

	return (
		<Timeline
			position="right"
			sx={{
				m: 0,
				p: 0,
				// OQ-1c — the standard opposite-content gutter suppression, so the rail reads
				// as a compact left-aligned column instead of leaving a blank left gutter.
				'& .MuiTimelineItem-root:before': { display: 'none' }
			}}
		>
			{stages.map((stage, index) => {
				const meta = STAGE_META[stage.status] ?? STAGE_META.pending;
				const Icon = meta.Icon;
				const isLast = index === stages.length - 1;

				return (
					<TimelineItem key={stage.key}>
						{/* Decorative rail — status is never conveyed by colour alone: every dot is
						    paired with a distinct icon AND the text label/fact to the right
						    (NFR-Accessibility). */}
						<TimelineSeparator aria-hidden="true">
							<TimelineDot color={meta.color} variant={meta.variant}>
								<Icon fontSize="small" />
							</TimelineDot>
							{!isLast && <TimelineConnector />}
						</TimelineSeparator>
						<TimelineContent sx={{ pb: isLast ? 0 : 4 }}>
							<Typography variant="subtitle2" sx={{ color: meta.textColor }}>
								{stage.label}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{stage.fact}
							</Typography>
							{/* AC-6 — the failing stage LOCALISES the failure; it does not repeat the
							    text. The full reason lives once, in the above-the-fold banner (AC-1),
							    because a long ACR/Azure error rendered in two independently-scrolling
							    blocks on one screen defeats the legibility AC-1 exists to buy.
							    "see reason above" is appended only when a reason actually exists, so
							    the pointer can never point at nothing. */}
							{index === firstFailedIndex && (
								<Typography variant="caption" color="error.main">
									{run.ErrorMessage ? 'Failed here — see reason above' : 'Failed here'}
								</Typography>
							)}
							{/* deploy-pipelines-run-logs — the build log, inline on the Build
							    stage and only there (see shouldRenderBuildLog above). Keyed on
							    the run so a new run gets a freshly-derived default expansion
							    instead of inheriting the previous run's. */}
							{pipelineId && run.Id && shouldRenderBuildLog(stage) && (
								<BuildLogPanel key={run.Id} pipelineId={pipelineId} run={run} stageStatus={stage.status} />
							)}
						</TimelineContent>
					</TimelineItem>
				);
			})}
		</Timeline>
	);
};

export default React.memo(PipelineFlow);

PipelineFlow.propTypes = {
	// Threaded down from PipelineDetail via RunConsole purely so the Build stage's log panel
	// can key its request. No new page-level state and no new page-level request were added
	// for it — the panel owns the one log fetch, and Wave 1's single-run-poller invariant is
	// untouched.
	pipelineId: PropTypes.string,
	run: PropTypes.shape({
		Id: PropTypes.string,
		Status: PropTypes.number,
		ImageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		ErrorMessage: PropTypes.string,
		DockerImageMetaDataId: PropTypes.string
	}),
	// Display-only elapsed-so-far label for the currently active stage (e.g. "1m 04s"),
	// ticked by the parent's zero-network timer (AC-17) — this component issues no request
	// and owns no timer itself.
	elapsedLabel: PropTypes.string
};
